<template>
  <div class="mx-auto max-w-3xl space-y-8 pb-4">
    <header>
      <h1 class="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
        {{ t('landing.set_lock.title') }}
      </h1>
    </header>
    <div
      class="space-y-8 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm ring-1 ring-gray-100 sm:p-8"
    >
      <p class="text-gray-700 leading-relaxed">
        {{ t('landing.set_lock.intro') }}
      </p>
      <div>
        <h2 class="font-semibold text-gray-900">
          {{ t('landing.set_lock.direct_title') }}
        </h2>
        <p class="mt-3 whitespace-pre-line text-gray-700 leading-relaxed">
          {{ t('landing.set_lock.direct_body') }}
        </p>
      </div>
      <div>
        <h2 class="font-semibold text-gray-900">
          {{ t('landing.set_lock.plate_title') }}
        </h2>
        <p class="mt-3 text-gray-700 leading-relaxed">
          {{ t('landing.set_lock.plate_lead') }}
        </p>
        <p class="mt-4 whitespace-pre-line text-gray-700 leading-relaxed">
          {{ t('landing.set_lock.plate_models_body') }}
        </p>
      </div>
      <p class="text-gray-700 leading-relaxed">
        {{ t('landing.set_lock.stainless_note') }}
      </p>
      <section
        class="border-t border-gray-100 pt-8"
        :aria-label="t('landing.set_lock.images_heading')"
      >
        <h2 class="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {{ t('landing.set_lock.images_heading') }}
        </h2>
        <div
          class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3"
        >
          <figure
            v-for="n in setLockImageIndexes"
            :key="n"
            class="flex flex-col overflow-hidden rounded-2xl border border-gray-100 bg-gradient-to-b from-gray-50/90 to-white shadow-sm ring-1 ring-gray-100/80"
          >
            <button
              type="button"
              class="group relative w-full cursor-zoom-in text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              :aria-label="`${t('landing.set_lock.lightbox_open_hint')}: ${t(`landing.set_lock.image_alt_${n}`)}`"
              @click="openLightbox(n)"
            >
              <div
                class="relative flex aspect-[4/3] w-full items-center justify-center bg-gray-50/50 p-3 sm:p-4"
              >
                <img
                  :src="`/images/help/set-lock-${n}.jpg`"
                  :alt="t(`landing.set_lock.image_alt_${n}`)"
                  class="pointer-events-none max-h-full max-w-full object-contain transition duration-300 group-hover:scale-[1.02]"
                  loading="lazy"
                  decoding="async"
                >
              </div>
            </button>
            <figcaption
              class="border-t border-gray-100 bg-white/90 px-3 py-2.5 text-center text-xs font-medium text-gray-500"
            >
              {{ t('landing.set_lock.image_caption', { n }) }}
            </figcaption>
          </figure>
        </div>
      </section>
    </div>

    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="lightboxIndex !== null"
          class="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
          role="dialog"
          aria-modal="true"
          :aria-label="t('landing.set_lock.images_heading')"
        >
          <div
            class="absolute inset-0 bg-gray-950/85 backdrop-blur-[2px]"
            aria-hidden="true"
            @click="closeLightbox"
          />
          <div
            class="relative z-10 flex w-full max-w-[min(96vw,1200px)] flex-col"
          >
            <div class="mb-3 flex shrink-0 justify-end sm:mb-4">
              <button
                type="button"
                class="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-white px-3 text-sm font-medium text-gray-900 shadow-lg ring-1 ring-gray-200/90 transition hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 sm:px-4"
                :aria-label="t('landing.set_lock.lightbox_close')"
                @click="closeLightbox"
              >
                <UIcon name="i-heroicons-x-mark-20-solid" class="h-5 w-5 shrink-0" />
                <span class="hidden sm:inline">{{ t('landing.set_lock.lightbox_close') }}</span>
              </button>
            </div>
            <div
              class="flex max-h-[min(82vh,860px)] min-h-0 w-full items-center justify-center overflow-auto rounded-xl bg-white/5 p-2 sm:p-4"
              @click.stop
            >
              <img
                v-if="lightboxIndex !== null"
                :src="`/images/help/set-lock-${lightboxIndex}.jpg`"
                :alt="t(`landing.set_lock.image_alt_${lightboxIndex}`)"
                class="max-h-[min(78vh,820px)] w-auto max-w-full object-contain shadow-2xl"
                decoding="async"
              >
            </div>
            <p class="mt-3 text-center text-sm text-white/90 drop-shadow">
              {{ lightboxIndex !== null ? t('landing.set_lock.image_caption', { n: lightboxIndex }) : '' }}
            </p>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
const { t } = useI18n()

const setLockImageIndexes = [1, 2, 3] as const

const lightboxIndex = ref<null | 1 | 2 | 3>(null)

function openLightbox(n: 1 | 2 | 3) {
  lightboxIndex.value = n
}

function closeLightbox() {
  lightboxIndex.value = null
}

function onEscape(e: KeyboardEvent) {
  if (e.key === 'Escape' && lightboxIndex.value !== null) {
    closeLightbox()
  }
}

watch(lightboxIndex, (v) => {
  if (!import.meta.client) {
    return
  }
  document.body.style.overflow = v !== null ? 'hidden' : ''
})

onMounted(() => {
  if (import.meta.client) {
    window.addEventListener('keydown', onEscape)
  }
})

onUnmounted(() => {
  if (import.meta.client) {
    window.removeEventListener('keydown', onEscape)
    document.body.style.overflow = ''
  }
})

useHead(() => ({
  title: t('landing.set_lock.meta_title'),
  meta: [{ name: 'description', content: t('landing.set_lock.meta_description') }],
}))
</script>
